import { memo } from 'react';
import { useLocation } from 'react-router-dom';

import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import {
    getCustomListCatalogItem,
    getCustomListCatalogItemId,
} from 'entities/CustomList';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { useGetItemDataUpdate } from 'features/CatalogItemCheckbox/lib/useGetItemDataUpdate';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';

import type { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { CustomListItem } from 'entities/CustomList';

interface OrderListItemAddCellProps {
    listItem: CustomListItem;
    listId: string;
}

interface OrderListItemAddControlProps {
    catalogItem: CatalogItem;
    catalogItemId: string;
    listId: string;
    targetQuantity: number;
    quickEdit: boolean;
}

const OrderListItemAddControl = ({
    catalogItem,
    catalogItemId,
    listId,
    targetQuantity,
    quickEdit,
}: OrderListItemAddControlProps) => {
    const identity = useCatalogIdentity();
    const selectionKey = getItemUid(catalogItem, identity);
    const { isActive, idToEdit, itemIsInOrder, qty } = useGetItemDataUpdate({
        item: catalogItem,
    });

    return (
        <CatalogItemCheckbox
            showQtyCheckbox
            isActive={isActive}
            quickEdit={quickEdit}
            qty={qty ?? 0}
            selectionKey={selectionKey}
            itemUid={selectionKey}
            catalogItemId={catalogItemId}
            itemIdToEdit={idToEdit}
            isInOrder={itemIsInOrder}
            source="lists"
            categoryId={catalogItem.categoryId}
            title={catalogItem.title}
            brand={catalogItem.brand}
            thumbnailUrl={catalogItem.thumbnailUrl}
            sku={catalogItem.sku}
            propertyCategoryId={catalogItem.categoryId}
            addQuantity={targetQuantity}
            listId={listId}
        />
    );
};

export const OrderListItemAddCell = memo(
    ({ listItem, listId }: OrderListItemAddCellProps) => {
        const location = useLocation();
        const catalogItem = getCustomListCatalogItem(listItem);
        const catalogItemId = getCustomListCatalogItemId(listItem);

        if (!catalogItem || !catalogItemId) {
            return null;
        }

        return (
            <OrderListItemAddControl
                catalogItem={catalogItem}
                catalogItemId={catalogItemId}
                listId={listId}
                targetQuantity={listItem.targetQuantity || 1}
                quickEdit={Boolean(location.state?.selectedOrderId)}
            />
        );
    },
);

OrderListItemAddCell.displayName = 'OrderListItemAddCell';
